import {
	RISK_SESSION_KEY,
	WELLBEING_LIMITS,
	WELLBEING_STORAGE_KEY,
} from '../constants/wellbeingMessages'

function getIsoWeekKey(date = new Date()) {
	const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
	const dayNum = d.getUTCDay() || 7
	d.setUTCDate(d.getUTCDate() + 4 - dayNum)
	const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
	const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7)
	return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`
}

function todayKey(date = new Date()) {
	return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
}

export function defaultWellbeingState() {
	return {
		weekKey: getIsoWeekKey(),
		showCount: 0,
		lastShownAt: 0,
		lastDismissedAt: 0,
		lastCompletedAt: 0,
		middayShownWeek: '',
		eveningShownDate: '',
		heavyShownDate: '',
		riskShownForId: '',
	}
}

export function readWellbeingState() {
	try {
		const raw = localStorage.getItem(WELLBEING_STORAGE_KEY)
		const parsed = raw ? JSON.parse(raw) : null
		const base = defaultWellbeingState()
		if (!parsed || typeof parsed !== 'object') return base
		const weekKey = getIsoWeekKey()
		if (parsed.weekKey !== weekKey) {
			return {
				...base,
				weekKey,
				lastDismissedAt: parsed.lastDismissedAt || 0,
				lastCompletedAt: parsed.lastCompletedAt || 0,
			}
		}
		return { ...base, ...parsed, weekKey }
	} catch {
		return defaultWellbeingState()
	}
}

export function writeWellbeingState(patch) {
	const next = { ...readWellbeingState(), ...patch, weekKey: getIsoWeekKey() }
	try {
		localStorage.setItem(WELLBEING_STORAGE_KEY, JSON.stringify(next))
	} catch {
		/* ignore */
	}
	return next
}

export function markWellbeingShown(scenario) {
	const now = Date.now()
	const state = readWellbeingState()
	const patch = {
		showCount: (state.showCount || 0) + 1,
		lastShownAt: now,
	}
	if (scenario === 'midday') patch.middayShownWeek = getIsoWeekKey()
	if (scenario === 'evening') patch.eveningShownDate = todayKey()
	if (scenario === 'heavy') patch.heavyShownDate = todayKey()
	return writeWellbeingState(patch)
}

export function markWellbeingDismissed() {
	return writeWellbeingState({ lastDismissedAt: Date.now() })
}

export function markWellbeingCompleted() {
	return writeWellbeingState({ lastCompletedAt: Date.now() })
}

export function markRiskSessionEnded({
	sessionId,
	type = 'risk',
	endedAt = Date.now(),
} = {}) {
	const payload = {
		sessionId: sessionId || `risk-${endedAt}`,
		type,
		endedAt,
	}
	try {
		localStorage.setItem(RISK_SESSION_KEY, JSON.stringify(payload))
	} catch {
		/* ignore */
	}
	return payload
}

export function readRiskSession() {
	try {
		const raw = localStorage.getItem(RISK_SESSION_KEY)
		return raw ? JSON.parse(raw) : null
	} catch {
		return null
	}
}

export function canShowWellbeingPopup(now = Date.now()) {
	const state = readWellbeingState()
	if ((state.showCount || 0) >= WELLBEING_LIMITS.MAX_PER_WEEK) {
		return { ok: false, reason: 'weekly_cap' }
	}
	if (state.lastShownAt && now - state.lastShownAt < WELLBEING_LIMITS.MIN_GAP_MS) {
		return { ok: false, reason: 'min_gap' }
	}
	if (
		state.lastDismissedAt &&
		now - state.lastDismissedAt < WELLBEING_LIMITS.DISMISS_COOLDOWN_MS
	) {
		return { ok: false, reason: 'dismiss_cooldown' }
	}
	if (
		state.lastCompletedAt &&
		now - state.lastCompletedAt < WELLBEING_LIMITS.COMPLETE_COOLDOWN_MS
	) {
		return { ok: false, reason: 'complete_cooldown' }
	}
	return { ok: true, state }
}

export function hasCompletedToday(now = new Date()) {
	const state = readWellbeingState()
	if (!state.lastCompletedAt) return false
	const completed = new Date(state.lastCompletedAt)
	return (
		completed.getFullYear() === now.getFullYear() &&
		completed.getMonth() === now.getMonth() &&
		completed.getDate() === now.getDate()
	)
}

export { getIsoWeekKey, todayKey }
